import type { Resource, ServiceRoleSummary, ServiceRoleWithCounts } from '@/api/mcp-appointments';

/** Display label for a service role (name preferred, key fallback). */
export function getServiceRoleLabel(
  role: Pick<ServiceRoleSummary, 'key' | 'name'> | null | undefined
): string {
  if (!role) {
    return '';
  }
  const name = role.name?.trim();
  if (name) {
    return name;
  }
  return role.key;
}

/** Whether a resource is linked to the given service role. */
export function resourceHasServiceRole(resource: Resource, serviceRoleId: string): boolean {
  if (!serviceRoleId) {
    return false;
  }
  return (resource.serviceRoles ?? []).some((link) => link.serviceRoleId === serviceRoleId);
}

/** Service role IDs already assigned to other requirement rows (excludes current row). */
export function collectUsedServiceRoleIds(
  requirements: Array<{ serviceRoleId?: string | null }> | undefined,
  excludeFieldIndex: number
): Set<string> {
  const used = new Set<string>();
  for (let index = 0; index < (requirements?.length ?? 0); index++) {
    if (index === excludeFieldIndex) {
      continue;
    }
    const id = requirements![index]?.serviceRoleId?.trim();
    if (id) {
      used.add(id);
    }
  }
  return used;
}

/** Filter catalog roles available for a requirement row picker. */
export function filterAvailableServiceRoles(
  catalog: ServiceRoleWithCounts[],
  usedByOtherCards: Set<string>,
  currentServiceRoleId: string | undefined
): ServiceRoleWithCounts[] {
  return catalog.filter((role) => {
    if (role.id === currentServiceRoleId) {
      return true;
    }
    return !usedByOtherCards.has(role.id);
  });
}

/** Extract serviceRoleIds from a resource's role links. */
export function serviceRoleIdsFromResource(resource: Pick<Resource, 'serviceRoles'>): string[] {
  return (resource.serviceRoles ?? []).map((link) => link.serviceRoleId);
}
